import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  animate,
  motion,
  useMotionValue,
  useMotionValueEvent,
  useReducedMotion,
  useTransform,
  type AnimationPlaybackControls,
  type MotionValue,
  type PanInfo,
} from "framer-motion";
import {
  Activity,
  ChevronLeft,
  ChevronRight,
  Rocket,
  SlidersHorizontal,
  Sparkles,
  Users,
  type LucideIcon,
} from "lucide-react";
import { HOME_CAPABILITIES, type HomeCapability } from "@/lib/route-content";
import { categoryColor } from "@/lib/category-colors";

/*
 * Stacked, fanned card carousel (after shadcnspace's carousel-07). One
 * progress value drives every card: a card's offset from the front sets its
 * slide, drop, tilt, scale and stacking, and the cards furthest out fade at
 * the seam where the loop wraps. Navigation: drag, the arrow buttons, the
 * arrow keys, or clicking a card behind the front one.
 */

const ICONS: Record<HomeCapability["category"], LucideIcon> = {
  Launch: Rocket,
  Manage: SlidersHorizontal,
  Monitor: Activity,
  Optimize: Sparkles,
  Collaborate: Users,
};

const CARDS = HOME_CAPABILITIES;
const N = CARDS.length;

/** Spacing per layout tier, chosen by the stage's own width. */
const TIERS = [
  { maxWidth: 640, x: 92, y: 18, rotate: 8, scale: 0.07, sensitivity: 180, distance: 120, velocity: 500 },
  { maxWidth: 1024, x: 140, y: 28, rotate: 10, scale: 0.09, sensitivity: 220, distance: 160, velocity: 650 },
  { maxWidth: Infinity, x: 180, y: 36, rotate: 11, scale: 0.11, sensitivity: 250, distance: 200, velocity: 800 },
];

const SPRING = { type: "spring", stiffness: 200, damping: 30, mass: 1 } as const;

/** Signed distance from the front, wrapped into (-N/2, N/2]. */
const wrap = (d: number) => {
  const m = ((d % N) + N) % N;
  return m > N / 2 ? m - N : m;
};

/** Teal → violet across the cards, matching the hero diagram's outputs. */
const mix = categoryColor;

export function CapabilityCarousel() {
  const reduceMotion = useReducedMotion();
  const stageRef = useRef<HTMLDivElement>(null);
  const progress = useMotionValue(0);
  const tier = useMotionValue(TIERS.length - 1);
  const target = useRef(0);
  const anim = useRef<AnimationPlaybackControls | null>(null);
  const panStart = useRef(0);
  const panned = useRef(false);
  const [active, setActive] = useState(0);

  // Layout effect, so phones and tablets never paint a frame at desktop spacing.
  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const update = () => tier.set(TIERS.findIndex((t) => el.clientWidth < t.maxWidth));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [tier]);

  useMotionValueEvent(progress, "change", (v) => {
    const i = ((Math.round(v) % N) + N) % N;
    setActive((prev) => (prev === i ? prev : i));
  });

  const goTo = useCallback(
    (to: number) => {
      target.current = to;
      anim.current?.stop();
      if (reduceMotion) progress.set(to);
      else anim.current = animate(progress, to, SPRING);
    },
    [progress, reduceMotion],
  );

  /**
   * Bring card `i` to the front by the shortest way round from where the
   * stack is drawn now, not from a target an unfinished spring is still
   * heading to: the visitor picked the card by its on-screen position.
   */
  const bringToFront = useCallback(
    (i: number) => {
      const base = Math.round(progress.get());
      goTo(base + wrap(i - base));
    },
    [goTo, progress],
  );

  const step = (dir: 1 | -1) => goTo(Math.round(target.current) + dir);

  const onPanStart = () => {
    anim.current?.stop();
    panned.current = true;
    panStart.current = progress.get();
  };
  const onPan = (_: PointerEvent, info: PanInfo) => {
    progress.set(progress.get() - info.delta.x / TIERS[tier.get()].sensitivity);
  };
  const onPanEnd = (_: PointerEvent, info: PanInfo) => {
    const t = TIERS[tier.get()];
    const shift = Math.round(-info.offset.x / t.distance - info.velocity.x / t.velocity);
    goTo(Math.round(panStart.current) + Math.max(-3, Math.min(3, shift)));
    // The click that ends a drag must not also bring a card forward.
    setTimeout(() => (panned.current = false), 0);
  };

  return (
    <div className="select-none">
      <motion.div
        ref={stageRef}
        role="region"
        aria-roledescription="carousel"
        aria-label="What Sidestep does"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
          e.preventDefault();
          step(e.key === "ArrowLeft" ? -1 : 1);
        }}
        onPanStart={onPanStart}
        onPan={onPan}
        onPanEnd={onPanEnd}
        className="relative mx-auto flex h-[420px] sm:h-[480px] lg:h-[520px] max-w-6xl items-center justify-center rounded-3xl cursor-grab active:cursor-grabbing touch-pan-y focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      >
        {CARDS.map((card, i) => (
          <CapabilityCard
            key={card.category}
            card={card}
            index={i}
            progress={progress}
            tier={tier}
            onSelect={() => {
              if (!panned.current) bringToFront(i);
            }}
          />
        ))}
      </motion.div>

      <p className="sr-only" aria-live="polite">
        {`Showing ${active + 1} of ${N}: ${CARDS[active].category}`}
      </p>

      {/* No position dots: in a loop the front card is always the visual
          middle, so a row of dots would read as "first of five" while the
          stack shows a centre card. */}
      <div className="mt-4 flex items-center justify-center gap-4">
        <ArrowButton label="Previous" onClick={() => step(-1)}>
          <ChevronLeft className="h-5 w-5" />
        </ArrowButton>
        <ArrowButton label="Next" onClick={() => step(1)}>
          <ChevronRight className="h-5 w-5" />
        </ArrowButton>
      </div>
    </div>
  );
}

function ArrowButton({ label, onClick, children }: { label: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="grid h-12 w-12 place-items-center rounded-full border border-border bg-card text-foreground shadow-card transition-all duration-200 hover:-translate-y-0.5 hover:shadow-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
    >
      {children}
    </button>
  );
}

interface CapabilityCardProps {
  card: HomeCapability;
  index: number;
  progress: MotionValue<number>;
  tier: MotionValue<number>;
  onSelect: () => void;
}

function CapabilityCard({ card, index, progress, tier, onSelect }: CapabilityCardProps) {
  const offset = useTransform(progress, (p) => wrap(index - p));
  const x = useTransform([offset, tier], ([o, t]: number[]) => o * TIERS[t].x);
  const y = useTransform([offset, tier], ([o, t]: number[]) => (Math.abs(o) < 0.05 ? 0 : Math.abs(o) * TIERS[t].y));
  const rotate = useTransform([offset, tier], ([o, t]: number[]) => (Math.abs(o) < 0.05 ? 0 : o * TIERS[t].rotate));
  const scale = useTransform([offset, tier], ([o, t]: number[]) => 1 - Math.abs(o) * TIERS[t].scale);
  const opacity = useTransform(offset, (o) => {
    const a = Math.abs(o);
    return a > N / 2 - 0.5 ? Math.max(0, (N / 2 - a) / 0.5) : 1;
  });
  const zIndex = useTransform(offset, (o) => Math.round(100 - Math.abs(o) * 10));
  const dim = useTransform(offset, (o) => Math.min(0.5, Math.abs(o) * 0.22));

  const t = index / (N - 1);
  const Icon = ICONS[card.category];
  // Fine flowing lines behind the copy, tinted with the card's colour.
  const lines = useMemo(
    () =>
      Array.from({ length: 7 }, (_, j) => (
        <path
          key={j}
          d={`M${-20 + j * 55},0 C${40 + j * 40},140 ${300 - j * 20},170 320,${120 + j * 36}`}
          stroke={mix(t, 0.35)}
          strokeWidth="1"
          fill="none"
        />
      )),
    [t],
  );

  return (
    <motion.div
      role="group"
      aria-roledescription="slide"
      aria-label={`${index + 1} of ${N}: ${card.category}`}
      onClick={onSelect}
      style={{
        x,
        y,
        rotate,
        scale,
        opacity,
        zIndex,
        background: `radial-gradient(120% 70% at 20% 0%, ${mix(t, 0.38)}, transparent 60%), linear-gradient(165deg, hsl(226 45% 14%), hsl(228 50% 7%))`,
      }}
      className="absolute h-[340px] w-[236px] sm:h-[400px] sm:w-[280px] lg:h-[420px] lg:w-[300px] overflow-hidden rounded-[20px] sm:rounded-3xl text-left text-white shadow-[0_24px_50px_-24px_hsl(228_50%_7%/0.6)]"
    >
      <svg aria-hidden className="pointer-events-none absolute inset-0 h-full w-full opacity-55" viewBox="0 0 300 420" preserveAspectRatio="none">
        {lines}
      </svg>

      <div
        className="absolute left-5 top-5 sm:left-[22px] sm:top-[22px] grid h-11 w-11 sm:h-14 sm:w-14 place-items-center rounded-2xl"
        style={{
          background: `linear-gradient(135deg, ${mix(t)}, ${mix(Math.min(1, t + 0.25))})`,
          boxShadow: `0 10px 30px -8px ${mix(t)}`,
        }}
      >
        <Icon className="h-5 w-5 sm:h-[26px] sm:w-[26px]" aria-hidden />
      </div>
      <span className="absolute right-4 top-4 sm:right-5 sm:top-5 rounded-full bg-white/95 px-2.5 py-1 sm:px-3 sm:py-1.5 text-[10px] sm:text-[10.5px] font-extrabold uppercase tracking-[0.14em] text-[hsl(225_49%_12%)]">
        {card.category}
      </span>

      <div className="absolute inset-x-5 bottom-5 sm:inset-x-6 sm:bottom-6">
        <h3 className="text-[19px] sm:text-[23px] font-extrabold leading-[1.15] tracking-[-0.01em]">{card.title}</h3>
        <p className="mb-3 mt-2 sm:mb-4 sm:mt-2.5 text-[12.5px] sm:text-[13.5px] leading-[1.55] text-white/70">{card.desc}</p>
        <ul className="flex flex-wrap gap-1.5" aria-label={`${card.category} features`}>
          {card.features.map((f) => (
            <li key={f} className="rounded-full border border-white/15 bg-white/10 px-2.5 py-1 text-[11px] sm:text-[11.5px] font-semibold">
              {f}
            </li>
          ))}
        </ul>
      </div>

      <motion.div aria-hidden className="pointer-events-none absolute inset-0 rounded-[inherit] bg-black" style={{ opacity: dim }} />
    </motion.div>
  );
}
